/**
 * OffboardConfirmDialog (Sprint 5 S5-B-components)
 *
 * Two-step confirmation dialog for the FI Staff off-board action.
 * Off-boarding is permanent: role bindings are soft-deleted, the user
 * cannot be reinstated, and the audit row is the operator-of-record.
 * The two-step UX raises the friction enough that a single mis-click
 * cannot produce an irreversible state change.
 *
 * Step 1 -- warning + reason textarea (>= 10 chars). [Continue] advances.
 * Step 2 -- type the staff member's email verbatim to enable
 * [Confirm Off-board].
 *
 * Composes `@aaimplatform/ui` Dialog, Alert, Textarea, Input, and Button
 * primitives with zero local re-implementations.
 *
 * A failed off-board renders inside the dialog on step 2, where the
 * confirm that failed was pressed (`errorMessage`, GH #2307): the dialog
 * stays open on failure, so an alert on the page behind it is hidden by
 * the modal.
 */

import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  Input,
  Textarea,
} from '@aaimplatform/ui';

const MIN_REASON_LENGTH = 10;

export interface OffboardConfirmDialogProps {
  /** Controls dialog visibility. Mirrors React Aria modal contract. */
  open: boolean;
  /** Visibility change callback (close button, Escape key, backdrop). */
  onOpenChange: (open: boolean) => void;
  /** Display name for the staff member being off-boarded. */
  staffName: string;
  /** Email of the staff member. Used as the step 2 confirmation token. */
  staffEmail: string;
  /**
   * Submit handler. Receives the trimmed reason string. The parent owns
   * the network call (`useOffboardStaff`) and the step-up token.
   */
  onConfirm: (reason: string) => Promise<void>;
  /** Disables the submit button while the parent mutation is in flight. */
  isPending?: boolean;
  /**
   * The parent mutation's failure, rendered on step 2. The parent clears it
   * (`mutation.reset()`) when the dialog closes.
   */
  errorMessage?: string;
}

type Step = 'warning' | 'confirm';

/**
 * OffboardConfirmDialog -- Sprint 5 two-step confirmation surface for
 * the FI Staff off-board action. Pure UI; the parent wires the mutation
 * and step-up.
 */
export function OffboardConfirmDialog({
  open,
  onOpenChange,
  staffName,
  staffEmail,
  onConfirm,
  isPending = false,
  errorMessage,
}: OffboardConfirmDialogProps) {
  const [step, setStep] = useState<Step>('warning');
  const [reason, setReason] = useState('');
  const [reasonTouched, setReasonTouched] = useState(false);
  const [emailConfirm, setEmailConfirm] = useState('');

  // Reset transient state whenever the dialog closes so a re-open starts
  // from a clean slate. Prevents the prior staff member's tokens leaking
  // into the next off-board attempt.
  useEffect(() => {
    if (!open) {
      setStep('warning');
      setReason('');
      setReasonTouched(false);
      setEmailConfirm('');
    }
  }, [open]);

  const trimmedReason = reason.trim();
  const reasonValid = trimmedReason.length >= MIN_REASON_LENGTH;
  const showReasonError = reasonTouched && !reasonValid;

  const emailMatches = emailConfirm.trim() === staffEmail;

  const handleAdvance = () => {
    setReasonTouched(true);
    if (!reasonValid) return;
    setStep('confirm');
  };

  const handleBack = () => {
    if (isPending) return;
    setStep('warning');
  };

  const handleConfirm = async () => {
    if (!emailMatches || !reasonValid || isPending) return;
    await onConfirm(trimmedReason);
  };

  const handleCancel = () => {
    if (isPending) return;
    onOpenChange(false);
  };

  if (step === 'warning') {
    return (
      <Dialog
        open={open}
        onOpenChange={onOpenChange}
        title={`Off-board ${staffName}`}
        size="md"
        isDismissable={!isPending}
        isKeyboardDismissDisabled={isPending}
        footer={
          <>
            <Button
              variant="tertiary"
              onClick={handleCancel}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAdvance}
              disabled={!reasonValid || isPending}
            >
              Continue
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Alert type="warning" title="Permanent action">
            Off-boarding is permanent. The user cannot be reinstated. All
            role bindings will be soft-deleted.
          </Alert>
          <Textarea
            label="Reason"
            value={reason}
            onChange={(value) => {
              setReason(value);
              if (!reasonTouched) setReasonTouched(true);
            }}
            onBlur={() => setReasonTouched(true)}
            required
            rows={4}
            maxLength={500}
            showCharCount
            fullWidth
            helperText={`Minimum ${MIN_REASON_LENGTH} characters. Recorded on the staff member's audit trail.`}
            error={
              showReasonError
                ? `Provide at least ${MIN_REASON_LENGTH} characters.`
                : undefined
            }
            placeholder="Explain the operational reason for off-boarding."
          />
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Off-board ${staffName}`}
      size="md"
      isDismissable={!isPending}
      isKeyboardDismissDisabled={isPending}
      footer={
        <>
          <Button
            variant="tertiary"
            onClick={handleBack}
            disabled={isPending}
          >
            Back
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              void handleConfirm();
            }}
            disabled={!emailMatches || !reasonValid || isPending}
          >
            {isPending ? 'Off-boarding...' : 'Confirm Off-board'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {errorMessage && (
          <Alert type="error" title="Off-board failed">
            {errorMessage}
          </Alert>
        )}
        <p className="text-sm font-sans text-secondary">
          Type{' '}
          <span className="font-mono text-primary">{staffEmail}</span> to
          confirm. The string must match exactly.
        </p>
        <Input
          label="Email confirmation"
          type="email"
          value={emailConfirm}
          onChange={(event) => setEmailConfirm(event.target.value)}
          autoComplete="off"
          spellCheck={false}
          fullWidth
          placeholder={staffEmail}
        />
      </div>
    </Dialog>
  );
}

export default OffboardConfirmDialog;
